import React, { useEffect } from 'react';
import clsx from 'clsx';

export interface ActivityDrawerProps {
  onClose: () => void;
  children: React.ReactNode;
  /** Width class. Defaults to the standard w-96 activity slide-over. */
  widthClassName?: string;
}

/**
 * The right-side activity slide-over.
 *
 * This chrome was hand-rolled at fifteen call sites and had drifted into two
 * incompatible looks: half of them `bg-surface` with a
 * `border-hairline` edge and a `bg-black/20` scrim, the other half already on
 * glass. Same panel, same content, two different surfaces depending on which
 * page you opened it from.
 *
 * One shell now: a glass panel with the quiet ink backdrop. The backdrop is
 * `bg-ink/20 backdrop-blur-[2px]` — never `bg-black/*`, which belongs only
 * over images and video.
 */
const ActivityDrawer: React.FC<ActivityDrawerProps> = ({
  onClose,
  children,
  widthClassName = 'w-96',
}) => {
  // One site had an Escape handler, on a div that could never hold focus. It
  // belongs on the document, and it belongs to every drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
  <>
    <div
      className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px] transition-opacity duration-300"
      onClick={onClose}
      aria-hidden
    />
    <div
      className={clsx(
        'fixed inset-y-0 right-0 z-50 flex flex-col glass !border-y-0 !border-r-0 shadow-xl animate-slide-in-right',
        widthClassName
      )}
    >
      {children}
    </div>
  </>
  );
};

export default ActivityDrawer;
